import Accordion from "@bmi-digital/components/accordion";
import Section from "@bmi-digital/components/section";
import Tabs from "@bmi-digital/components/tabs";
import Typography from "@bmi-digital/components/typography";
import { replaceSpaces, transformHyphens } from "@bmi-digital/components/utils";
import Tab from "@mui/material/Tab";
import { graphql } from "gatsby";
import React from "react";
import RichText from "../components/RichText";
import withGTM from "../utils/google-tag-manager";
import type { TabProps } from "@mui/material/Tab";
import type { AccordionSummaryProps } from "@bmi-digital/components/accordion";
import type { PayloadTabsOrAccordionSection } from "../types/payload";
import type { TitleWithContentForTabsAndAccordionData } from "./TitleWithContentForTabsAndAccordion";

const GTMAccordionSummary = withGTM<AccordionSummaryProps>(Accordion.Summary);

export type TabsOrAccordionSectionData = Pick<
  PayloadTabsOrAccordionSection,
  "blockType" | "type" | "title" | "description"
> & {
  items: readonly [
    TitleWithContentForTabsAndAccordionData,
    TitleWithContentForTabsAndAccordionData,
    ...TitleWithContentForTabsAndAccordionData[]
  ];
};

const SectionAccordion = ({
  items
}: {
  items: readonly TitleWithContentForTabsAndAccordionData[];
}) => {
  return (
    <>
      {items.length > 0 && (
        <Accordion>
          {items.map(({ title, content }) => {
            return (
              <Accordion.Item key={title}>
                <GTMAccordionSummary
                  gtm={{
                    id: "selector-accordion1",
                    label: title,
                    action: "Selector – Accordion"
                  }}
                >
                  <Typography component="h3" variant="h6">
                    {transformHyphens(title)}
                  </Typography>
                </GTMAccordionSummary>
                <Accordion.Details>
                  <RichText document={content} hasNoBottomMargin />
                </Accordion.Details>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}
    </>
  );
};

const SectionTabs = ({
  items
}: {
  items: readonly TitleWithContentForTabsAndAccordionData[];
}) => {
  const GTMTab = withGTM<TabProps>(Tab, {
    label: "label"
  });

  return (
    <Tabs
      tabComponent={(props: TabProps) => (
        <GTMTab
          gtm={{ id: "selector-tabs2", action: "Selector – Tabs" }}
          {...props}
        />
      )}
      color="primary"
      initialValue={items.length > 0 ? items[0].title : null}
    >
      {items.length > 0 &&
        items.map(({ title, content }) => {
          return (
            <Tabs.TabPanel heading={title} index={title} key={title}>
              <RichText document={content} />
            </Tabs.TabPanel>
          );
        })}
    </Tabs>
  );
};

const componentMap = {
  Accordion: SectionAccordion,
  Tabs: SectionTabs
};

const TabsOrAccordionSection = ({
  data
}: {
  data: TabsOrAccordionSectionData;
}) => {
  const { title, type, items, description } = data;
  const backgroundColor = type === "Tabs" ? "pearl" : "white";
  // eslint-disable-next-line security/detect-object-injection
  const Component = componentMap[type];

  return (
    <Section
      backgroundColor={backgroundColor}
      data-testid={`tabs-or-accordion-section-${replaceSpaces(title)}`}
    >
      {title && <Section.Title>{title}</Section.Title>}
      {description && <Typography variant="body1">{description}</Typography>}
      <div style={{ marginTop: "40px" }}>
        <Component items={items} />
      </div>
    </Section>
  );
};

export default TabsOrAccordionSection;

export const query = graphql`
  fragment TabsOrAccordionSectionFragment on PayloadTabsOrAccordionSection {
    blockType
    type
    title
    description
    items {
      ...TitleWithContentFragment
    }
  }
`;
